/**
 * Mixpanel abstraction to provide the same methods for both mobile and web
 * Under the hood, it calls the Mixpanel APIs specific to the platform it runs.
 */
import MixpanelBrowser, { Callback, RequestOptions } from 'mixpanel-browser';
import MixpanelRN from 'react-native-mixpanel';

let Mixpanel: Partial<typeof MixpanelRN & typeof MixpanelBrowser>;

/**
 * Set the MixPanel instance (React Native or Web).
 * Must be run on the top-most component.
 * @param instance MixPanel
 * @returns void
 */
const setInstance = (instance: typeof Mixpanel) => {
  Mixpanel = instance;
};

/**
 * Initialize a new MixPanel object.
 * @param token Your MixPanel API token
 * @param debug (optional) Run this MixPanel instance in debug mode. Logs output in the console (Web only).
 * @returns void
 */
const initialize = (token: string, debug = false) => {
  // Web
  if (Mixpanel?.init) {
    Mixpanel?.init?.(token, {
      // Run some stuff after Mixpanel has loaded
      loaded(mp) {
        mp.set_config({ debug });
      },
    });

    return;
  }

  // React Native
  Mixpanel?.sharedInstanceWithToken?.(token);
};

/**
 * Set key-value pairs that will be included with all the events sent to Mixpanel
 * @param props Any JSON object
 * @returns void
 */
const registerSuperProperties = (props: Object) =>
  Mixpanel?.registerSuperProperties?.(props) || Mixpanel?.register?.(props);

/**
 * Identify a user with a unique ID to track activity across devices
 * @param uniqueID A string that uniquely identifies a user
 * @returns void
 */
const identify = (uniqueID: string) => Mixpanel?.identify?.(uniqueID);

/**
 * Set properties on a user record.
 * @param props Any JSON object
 * @returns void
 */
const setUserProperties = (props: Object) =>
  Mixpanel?.set?.(props) || Mixpanel?.people?.set?.(props);

/**
 * Clear super properties and generate a new random distinct ID for this instance.
 * Useful when the user logs out.
 * @returns void
 */
const reset = () => Mixpanel?.reset?.();

/**
 * Track the time it took for an action to occur. Running this function marks the start
 * of the action. To mark the end of an action, call the .trackEvent function using the same
 * event name.
 *
 * Example:
 * Mixpanel.timeEvent('Image Upload') // when starting uploading
 * Mixpanel.trackEvent('Image Upload') // when upload finishes
 *
 * @param eventName The name of the event you want to time
 * @returns void
 */
const timeEvent = (eventName: string) =>
  Mixpanel?.timeEvent?.(eventName) || Mixpanel?.time_event?.(eventName);

/**
 * Track an event. Most important and frequently used Mixpanel function.
 * @param eventName The name of the event you want to track. Usually, the format is "Screen - Action".
 * @param props (optional) Any JSON object
 * @param optionsOrCallback (optional) RequestOptions or callback function. Web only.
 * @param callback (optional) Callback function. Web only.
 * @returns void
 */
const trackEvent = (
  eventName: string,
  props?: { [key: string]: string } & Partial<{
    Item: string;
    Error: string;
    Origin: string;
    'Event Type': string;
    'Screen Type': string;
  }>,
  optionsOrCallback?: RequestOptions | Callback,
  callback?: Callback
) => {
  // To make showing the events in the correct order easier we add a timestamp prop
  // https://community.mixpanel.com/ideas/be-able-to-capture-milliseconds-on-events-3276
  const timeStampProp = {
    'Time MS': Date.now(),
  };

  const propsWithTimeStamp = { ...props, ...timeStampProp };

  Mixpanel?.trackWithProperties?.(eventName, propsWithTimeStamp) ||
    Mixpanel?.track?.(
      eventName,
      propsWithTimeStamp,
      optionsOrCallback,
      callback
    );
};

/**
 * Opt the user in to tracking
 * @returns void
 */
const optInTracking = () =>
  Mixpanel?.optInTracking?.() || Mixpanel?.opt_in_tracking?.();

/**
 * Opt the user out of data tracking
 */
const optOutTracking = () =>
  Mixpanel?.optOutTracking?.() || Mixpanel?.opt_out_tracking?.();

export default {
  setInstance,
  initialize,
  registerSuperProperties,
  identify,
  setUserProperties,
  reset,
  timeEvent,
  trackEvent,
  optInTracking,
  optOutTracking,
};
